'use client';

import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';
import map from 'lodash/map';
import cn from 'classnames';
import IconLink from './iconLink';
import styles from './headerPanel.module.scss';
import { KibSearch, KibSuperSelectOption } from '@chewy/kib-fields-new-react';
import '@chewy/kib-fields-new-styles/dist/kib-fields-new.css';
import { navigateToAnchor, navigateToLink, navigateToSearchResults } from '@/utils/helpCenterUtils';
import retrieveAutocompleteSearchResults from '@/actions/retrieve-autocomplete-search-results';
import { useRouter } from 'next/navigation';
import OHCContext from '@/providers/OHCContext';
import { useTrack } from '@chewy/use-analytics';
import URLGenerator from '@/utils/URLGenerator';
import { useDesktopMode } from '@/hooks/useDesktopMode';
import HeaderDogIcon from '@/components/icons/header-dog.svg';
import CloudHeaderIcon from '@/components/icons/cloud-header.svg';
import BreadcrumbsContainer from '../nav/BreadcrumbsContainer';
import { useDebouncedCallback } from 'use-debounce';
import { KibSearchIcon, KibCloseIcon } from '@chewy/kib-icons-react';
import { isRXTopicSelected } from '@/utils/helpCenterUtils';
export interface HeaderPanelProps {
  readonly className?: string;
}

const getFilteredOptions = (value: string, options: KibSuperSelectOption[] | any): (string | KibSuperSelectOption)[] => {
  if (!value) {
    return options;
  }
  const lowerCaseValue = value.toLowerCase();
  return options.filter((option: any) => {
    const label = typeof option === 'string' ? option : option?.label;
    return label.toLowerCase().includes(lowerCaseValue);
  });
};

const HeaderPanel = ({ className }: HeaderPanelProps): React.ReactElement => {
  const [searchValue, setSearchValue] = useState('');
  const { selectedTopic, isMobileApp, selectedArticle, categoryIdsMap } = useContext(OHCContext);
  const [selectedData, setSelectedData] = useState<KibSuperSelectOption | string | any | null>(null);
  const [filteredOptions, setFilteredOptions] = useState<(string | KibSuperSelectOption)[]>([]);
  const router = useRouter();
  const isRXTopic = useMemo(() => isRXTopicSelected(selectedTopic?.categoryId, categoryIdsMap), [selectedTopic, categoryIdsMap]);
  const generalPhone = '1-800-672-4399';
  const rxPhone = '1-877-977-3879';
  const phoneNumber = useMemo(() => (isRXTopic ? rxPhone : generalPhone), [isRXTopic]);
  const track = useTrack();
  const isDesktopMode = useDesktopMode();
  const shouldShowHeader = useMemo(
    () => isDesktopMode || (!isDesktopMode && !selectedArticle && !isMobileApp) || (isMobileApp && !selectedTopic),
    [isDesktopMode, selectedArticle, isMobileApp, selectedTopic]
  );
  const abortRef = useRef<AbortController | null>(null);

  const handleChangeValue = (data: any) => {
    const newValue = typeof data === 'string' ? data : data?.label || '';
    setSearchValue(newValue);
    setSelectedData(data);

    track(`Search Bar Text Changed`, {
      id: 'search-bar-text-changed-id',
      parentId: 'search-bar-id',
      level: 'help-center',
      type: 'search-bar',
      position: 1,
      event_name: 'Text Bar Click',
      eventAction: 'click',
      eventCategory: 'help-center-search-bar',
      eventLabel: 'help-center-search-bar-text-click',
      name: 'Search Bar Text Bar Changed',
      searchValue: newValue
    });
  };

  const doOnSubmit = (e: any) => {
    e.preventDefault();

    if (selectedData) {
      if (typeof selectedData === 'string') {
        navigateToSearchResults(encodeURIComponent(selectedData || searchValue), router);
      } else if (selectedData?.type === 'KEYWORD') {
        navigateToSearchResults(encodeURIComponent(selectedData?.value?.text || ''), router);
      } else if (selectedData?.value?.id) {
        navigateToAnchor(selectedData?.value?.id, router);
      }
      setSearchValue('');
    }

    track(`Search Bar Viewed`, {
      id: 'search-bar-viewed-id',
      parentId: 'search-bar-id',
      level: 'help-center',
      type: 'search-bar',
      position: 1,
      event_name: 'Autocomplete Clicked',
      eventAction: 'click',
      eventCategory: 'help-center-search-bar',
      eventLabel: 'help-center-search-bar-autocomplete-click',
      name: 'Search Bar Autocomplete Clicked'
    });
  };

  const handleChatClick = () => {
    track(`Header Chat Button Clicked`, {
      id: 'search-bar-text-submitted-id',
      parentId: 'search-bar-id',
      level: 'help-center',
      type: 'section',
      position: 1,
      event_name: 'Text Bar Clicked',
      eventAction: 'click',
      eventCategory: 'help-center-search-bar',
      eventLabel: 'help-center-search-bar-text-submitted',
      name: 'Search Bar Text Submitted'
    });

    let urlGenerator = new URLGenerator(window.location.origin + '/csrb/nma');
    if (!isMobileApp) {
      urlGenerator = new URLGenerator(window.location.origin + window.location.pathname);
      urlGenerator.addParameter({ name: 'signInFromChat', value: 'true' });
    }

    if (isRXTopic) {
      urlGenerator.addParameter({ name: 'pageContext', value: 'RX' });
    }

    window.location.href = urlGenerator.generateURL();
  };

  const handleCallClick = () => {
    const eventProperties = {
      id: 'header-call-button-id',
      parentId: 'header-id',
      level: 'header',
      type: 'static',
      position: 2,
      ...(isRXTopic && { pageContext: 'RX' })
    };

    // Handle external links (http or https)
    navigateToLink(`tel:${phoneNumber}`, router);
    track('Header Call Button Clicked', eventProperties);
  };

  const getSuggestions = useDebouncedCallback(async (searchValue: string) => {
    if (!searchValue) {
      setFilteredOptions([]);
      return;
    }
    // Fetch autocomplete search results
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    let searchResultsOrig;

    try {
      searchResultsOrig = await retrieveAutocompleteSearchResults({ searchValue });

      if ('error' in searchResultsOrig) {
        console.error(`Error fetching autocomplete search results for q="${searchValue}": ${searchResultsOrig.error}`);
        setFilteredOptions([]);
        return;
      }
    } catch (error) {
      console.error(`Error fetching autocomplete search results for q="${searchValue}": ${error}`);
      setFilteredOptions([]);
      return;
    }

    const searchResults = map(searchResultsOrig?.results, (item: any) => ({
      label: item.text,
      value: item,
      type: item?.type
    }));
    const newOptions = getFilteredOptions(searchValue, searchResults || []);
    setFilteredOptions(newOptions);
  }, 125);

  useEffect(() => {
    getSuggestions(searchValue);
  }, [searchValue, getSuggestions]);

  useEffect(() => {
    track(`Search Bar Viewed`, {
      id: 'search-bar-viewed-id',
      parentId: 'search-bar-id',
      level: 'help-center',
      type: 'search-bar',
      position: 1,
      event_name: 'Widget Viewed',
      eventAction: 'impression',
      eventCategory: 'help-center-search-bar',
      eventLabel: 'help-center-search-bar-view',
      name: 'Search Bar Viewed'
    });
  }, [track]);

  console.log('isDesktopMode', isDesktopMode);
  console.log('isMobileApp', isMobileApp);
  console.log('Should show header:', shouldShowHeader);

  return (
    <div className={styles.headerContainer}>
      {!isDesktopMode && !isMobileApp && <BreadcrumbsContainer className={styles.mobileBreadcrumbs} />}
      {shouldShowHeader && (
        <>
          <header className={cn(styles.header, className)} role="banner">
            <form className={cn(styles.searchBar)} onSubmit={doOnSubmit} role="search" aria-label="Help Center Search">
              <KibSearch
                className={cn(styles.searchInput, isDesktopMode ? styles.searchInputDesktop : '')}
                value={searchValue}
                onChange={handleChangeValue}
                options={filteredOptions}
                placeholder="Start a return, or Ordering a prescription"
                label={`How can we help?`}
                typeAhead={3}
                aria-describedby="search-instructions"
                aria-expanded={filteredOptions.length > 0}
                aria-autocomplete="list"
                action={() => (
                  <div>
                    <button
                      data-testid="clear-search-button"
                      aria-label="Clear search"
                      disabled={searchValue === ''}
                      className={cn(styles.clearSearchButton, {
                        [styles['clearSearchButton--disabled'] as string]: searchValue === '',
                      })}
                      type="button"
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => handleChangeValue('')}
                    >
                      <KibCloseIcon />
                    </button>

                    <button
                      data-testid="submit-search-button"
                      aria-label="Submit search"
                      type="submit"
                      className={styles.submitButton}
                    >
                      <KibSearchIcon />
                    </button>
                  </div>
                )}
              />
              <div id="search-instructions" className="sr-only">
                Type your question to search for help articles and topics. Use arrow keys to navigate suggestions and press Enter to select.
              </div>
              <div className={styles.headerCloudIconContainer}>
                <CloudHeaderIcon width={75} aria-hidden="true" focusable="false" data-a11y="decorative" />
              </div>
              <div className={styles.headerDogIconContainer}>
                <HeaderDogIcon className={styles.headerDogIcon} aria-hidden="true" focusable="false" data-a11y="decorative" />
              </div>
            </form>
            {isDesktopMode && (
              <div className={styles.headerContacts} role="complementary" aria-label="Contact options">
                <span className={styles.contactDescr}>{`We’re here for you, 24/7.`}</span>
                <IconLink className={styles.contactIcons} onClick={handleChatClick} title="Chat now" icon="HCChatIcon" ariaLabel="Start a live chat session" />
                {/* {isRXTopic && (
                  <IconLink
                    className={styles.contactIcons}
                    onClick={handleEmailClick}
                    title="Email"
                    icon="EmailIcon"
                    ariaLabel="Email"
                  />
                )} */}
                <IconLink className={styles.contactIcons} onClick={handleCallClick} title={phoneNumber} icon="CallIcon" ariaLabel={`Call customer service at ${phoneNumber}`} />
              </div>
            )}
          </header>
        </>
      )}

      {isDesktopMode && !isMobileApp && <BreadcrumbsContainer className={styles.desktopBreadcrumbs} />}
    </div>
  );
};

export default HeaderPanel;
